import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

/** The narrowest expanded rail, in px. Below this the drag snaps to icons-only. */
export const RAIL_WIDTH_MIN = 200
/** The widest the rail may be dragged, in px. */
export const RAIL_WIDTH_MAX = 420
/** The rail's design width (`--spacing-rail` in @multaqa/tokens) and the reset target. */
export const RAIL_WIDTH_DEFAULT = 236

const RAIL_DISCLOSURE_STORAGE_KEY = 'multaqa.company.rail-disclosure'

interface RailDisclosure {
  expandedSections: Record<string, boolean>
  expandedGroups: Record<string, boolean>
}

function emptyRailDisclosure(): RailDisclosure {
  return { expandedSections: {}, expandedGroups: {} }
}

function booleanRecord(value: unknown): Record<string, boolean> {
  if (typeof value !== 'object' || value === null) return {}
  return Object.fromEntries(Object.entries(value).filter(([, item]) => typeof item === 'boolean'))
}

function readRailDisclosure(): RailDisclosure {
  if (typeof window === 'undefined') return emptyRailDisclosure()
  try {
    const stored = window.sessionStorage.getItem(RAIL_DISCLOSURE_STORAGE_KEY)
    if (!stored) return emptyRailDisclosure()
    const parsed = JSON.parse(stored) as Record<string, unknown>
    return {
      expandedSections: booleanRecord(parsed.expandedSections),
      expandedGroups: booleanRecord(parsed.expandedGroups),
    }
  } catch {
    return emptyRailDisclosure()
  }
}

function writeRailDisclosure(disclosure: RailDisclosure): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(RAIL_DISCLOSURE_STORAGE_KEY, JSON.stringify(disclosure))
  } catch {
    // Disclosure state is an enhancement; the rail remains usable without storage.
  }
}

const initialRailDisclosure = readRailDisclosure()

/**
 * Clamp a candidate rail width into the legal range, rounded to whole pixels.
 * Every write to `railWidth` goes through this — the persisted record can
 * therefore never hold a value a handle could not have produced.
 */
export function clampRailWidth(value: number): number {
  if (!Number.isFinite(value)) return RAIL_WIDTH_DEFAULT
  return Math.min(RAIL_WIDTH_MAX, Math.max(RAIL_WIDTH_MIN, Math.round(value)))
}

interface UiState {
  /**
   * Whether the module rail is showing as an overlay drawer on a narrow
   * viewport. Ignored from `lg` up, where the rail is always in flow.
   *
   * Replaces a `sidebarOpen` flag that had a toggle, a setter and no consumer
   * at all: the rail was a fixed 236px at every width, so at 375px it took 63%
   * of the viewport and left 139px for the content, with fifteen elements
   * overflowing. A new key rather than a rename so no stored `true` from the
   * old flag opens the drawer on first load.
   */
  railDrawerOpen: boolean
  theme: Theme
  /**
   * Explicit user choices for the rail's collapsible sections, keyed by the
   * section's `id` — true = expanded, false = collapsed. A section absent
   * from this map starts collapsed. It is kept in session storage, so a reload
   * preserves the user's working context while a new browser session starts
   * calm again.
   */
  expandedSections: Record<string, boolean>
  expandedGroups: Record<string, boolean>
  /**
   * The rail's expanded width in px (plans/59 N-02), as the user last dragged
   * or arrowed it. Persisted so the choice survives a reload. Icons-only mode
   * is deliberately NOT a width here — it is `labels: false` in the per-user
   * rail preferences — so while collapsed this value keeps holding the last
   * width >= RAIL_WIDTH_MIN and the drag handle's separator reports a value
   * that stays inside its own min/max.
   */
  railWidth: number
  setRailDrawerOpen: (open: boolean) => void
  setTheme: (theme: Theme) => void
  setRailWidth: (width: number) => void
  /**
   * Record the user's choice for a collapsible rail section. The store
   * cannot know a section's current effective state — that is derived in
   * the rail from the active route — so the caller passes it and the
   * recorded choice is its opposite.
   */
  toggleSection: (id: string, currentlyOpen: boolean) => void
  toggleGroup: (id: string, currentlyOpen: boolean) => void
  /**
   * The chart of accounts' folded parents, keyed by account id — true =
   * collapsed (plans/59 N-10). An account absent from this map is expanded,
   * so the map only grows when a parent is folded or everything is collapsed;
   * expand-all resets it to `{}` rather than recording a false per account.
   * Persisted so a fold survives a reload.
   */
  collapsedAccounts: Record<string, boolean>
  /** Fold or unfold one parent account's subtree. */
  toggleAccount: (id: string) => void
  /**
   * Fold or unfold every parent at once. The caller passes the parent ids —
   * this store holds no chart data — and expanding resets to an empty map,
   * keeping what is persisted as small as the user's actual folds.
   */
  setAllAccountsCollapsed: (ids: string[], collapsed: boolean) => void
}

/**
 * Genuinely client-side UI state, and nothing else.
 *
 * Server data belongs in TanStack Query. Filter, sort and pagination state
 * belongs in URL search params, so a view can be linked and reloaded. What is
 * left — chrome the user has toggled — lives here.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      railDrawerOpen: false,
      theme: 'light',
      expandedSections: initialRailDisclosure.expandedSections,
      expandedGroups: initialRailDisclosure.expandedGroups,
      railWidth: RAIL_WIDTH_DEFAULT,

      setRailDrawerOpen: (railDrawerOpen) => set({ railDrawerOpen }),
      setRailWidth: (railWidth) => set({ railWidth: clampRailWidth(railWidth) }),
      setTheme: (theme) => {
        set({ theme })
        applyTheme(theme)
      },
      toggleSection: (id, currentlyOpen) => {
        const expandedSections = { ...get().expandedSections, [id]: !currentlyOpen }
        set({ expandedSections })
        writeRailDisclosure({ expandedSections, expandedGroups: get().expandedGroups })
      },
      toggleGroup: (id, currentlyOpen) => {
        const expandedGroups = { ...get().expandedGroups, [id]: !currentlyOpen }
        set({ expandedGroups })
        writeRailDisclosure({ expandedSections: get().expandedSections, expandedGroups })
      },
      collapsedAccounts: {},
      toggleAccount: (id) =>
        set((state) => ({
          collapsedAccounts: { ...state.collapsedAccounts, [id]: !state.collapsedAccounts[id] },
        })),
      setAllAccountsCollapsed: (ids, collapsed) =>
        set(() => ({
          collapsedAccounts: collapsed ? Object.fromEntries(ids.map((id) => [id, true])) : {},
        })),
    }),
    {
      name: 'multaqa.company.ui',
      version: 2,
      migrate: (persistedState, version) => {
        if (version === 0 && typeof persistedState === 'object' && persistedState !== null) {
          const state = persistedState as Record<string, unknown>
          const {
            expandedSections: _expandedSections,
            expandedGroups: _expandedGroups,
            ...stateWithoutDisclosure
          } = state
          // `system` was the old implicit default, not an option exposed by the
          // toggle. Existing users should receive the new light default unless
          // they explicitly chose dark.
          return stateWithoutDisclosure.theme === 'system'
            ? { ...stateWithoutDisclosure, theme: 'light' }
            : stateWithoutDisclosure
        }
        if (version === 1 && typeof persistedState === 'object' && persistedState !== null) {
          const state = persistedState as Record<string, unknown>
          const {
            expandedSections: _expandedSections,
            expandedGroups: _expandedGroups,
            ...stateWithoutDisclosure
          } = state
          return stateWithoutDisclosure
        }
        return persistedState
      },
      partialize: ({
        expandedSections: _expandedSections,
        expandedGroups: _expandedGroups,
        ...state
      }) => state,
    },
  ),
)

/**
 * Reflect the theme onto `<html class="dark">`, which is what the `dark:`
 * variant in @multaqa/tokens keys off.
 */
export function applyTheme(theme: Theme): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark)

  document.documentElement.classList.toggle('dark', isDark)
}
